
import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { supabase } from '@/integrations/supabase/client';
import { ConversationService } from '@/services/conversationService';
import { chatImageService } from '@/services/chatImageService';
import { useChatRealtime, useChatTyping } from '@/hooks/useChatRealtime';
import { CHAT_ROLE } from '@/utils/ChatUtils';
import type { Message, Conversation, ChatContextType, PendingImageUpload } from '@/types/chat';

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

type ChatProviderProps = {
  children: ReactNode;
};

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [messagesByConversation, setMessagesByConversation] = useState<Record<string, Message[]>>({});
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [typingUsers, setTypingUsers] = useState<Record<string, { userId: string; conversationId: string; isTyping: boolean }>>({}); 
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  
  // Add state to track current business context to prevent cross-contamination
  const [currentBusinessContext, setCurrentBusinessContext] = useState<string | null>(null);
  
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();
  const { userBusinessId, isLoading: isBusinessLoading } = useBusiness();
  
  const fetchUnreadCountRef = useRef<NodeJS.Timeout | null>(null);

  // Clear chat state when navigating between different services/businesses
  const clearChatStateForNewBusiness = useCallback((businessId: string) => {
    console.log('[ChatContext] Clearing chat state for new business:', businessId);
    
    // Only clear if switching to a different business context
    if (currentBusinessContext !== businessId) {
      console.log('[ChatContext] Switching business context from', currentBusinessContext, 'to', businessId);
      
      setCurrentConversation(null);
      setCurrentBusinessContext(businessId);
      setLoadingMessages(false);
      
      // Clear typing users
      setTypingUsers({});
    }
  }, [currentBusinessContext]);

  // Helper function to get current conversation messages - enhanced with business context protection
  const getCurrentMessages = useCallback(() => {
    // Return empty array if no current conversation or if loading
    if (!currentConversation || loadingMessages) {
      console.log('[ChatContext] getCurrentMessages returning empty - currentConversation:', !!currentConversation, 'loadingMessages:', loadingMessages);
      return [];
    }
    
    // Return empty array if the current conversation doesn't match the current business context
    if (currentBusinessContext && currentConversation.business_id !== currentBusinessContext) {
      console.log('[ChatContext] getCurrentMessages returning empty - business context mismatch:', {
        currentBusinessContext,
        conversationBusinessId: currentConversation.business_id
      });
      return [];
    }
    
    const messages = messagesByConversation[currentConversation.id] || [];
    console.log('[ChatContext] getCurrentMessages returning', messages.length, 'messages for conversation', currentConversation.id);
    return messages;
  }, [currentConversation, messagesByConversation, loadingMessages, currentBusinessContext]);

  // Define markMessagesAsRead before it's used in useChatRealtime
  const markMessagesAsRead = async (conversationId: string): Promise<void> => {
    if (!user) return;
    
    console.log(`[ChatContext] Marking messages as read for conversation ${conversationId}`);
    
    try {
      await ConversationService.markMessagesAsRead(conversationId, user.id);
      
      // Update local message state for the specific conversation
      setMessagesByConversation(prev => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).map(msg => 
          msg.receiver_id === user.id && !msg.read_at
            ? { ...msg, read_at: new Date().toISOString() }
            : msg
        )
      }));
      
      // Update unread count using role-based counting
      const count = await ConversationService.getUnreadCountForRole(user.id, 'member');
      setTotalUnreadCount(count);
      
    } catch (error) {
      console.error('[ChatContext] Error marking messages as read:', error);
    }
  };

  // Handle incoming messages
  const handleMessageReceived = useCallback(async (newMessage: Message) => {
    // Fetch complete message data including images
    try {
      const { data: completeMessageData, error } = await supabase
        .from('messages')
        .select(`
          *,
          images:message_images (
            id,
            image_url,
            original_filename,
            file_size,
            display_order
          )
        `)
        .eq('id', newMessage.id)
        .single();

      if (error) {
        console.error('[ChatContext] Error fetching complete message data:', error);
        // Fallback to basic message if fetch fails
        setMessagesByConversation(prevMessages => {
          const conversationMessages = prevMessages[newMessage.conversation_id] || [];
          
          if (conversationMessages.some(msg => msg.id === newMessage.id)) {
            return prevMessages;
          }
          
          return {
            ...prevMessages,
            [newMessage.conversation_id]: [...conversationMessages, newMessage]
          };
        });
        return;
      }

      const messageWithImages = {
        ...completeMessageData,
        images: completeMessageData.images || []
      };

      setMessagesByConversation(prevMessages => {
        const conversationMessages = prevMessages[newMessage.conversation_id] || [];
        
        if (conversationMessages.some(msg => msg.id === newMessage.id)) {
          return prevMessages;
        }
        
        return {
          ...prevMessages,
          [newMessage.conversation_id]: [...conversationMessages, messageWithImages]
        };
      });
      
    } catch (error) {
      console.error('[ChatContext] Error in handleMessageReceived:', error);
      // Fallback to basic message
      setMessagesByConversation(prevMessages => {
        const conversationMessages = prevMessages[newMessage.conversation_id] || [];
        
        if (conversationMessages.some(msg => msg.id === newMessage.id)) {
          return prevMessages;
        }
        
        return {
          ...prevMessages,
          [newMessage.conversation_id]: [...conversationMessages, newMessage]
        };
      });
    }
  }, []);

  // Handle unread count updates
  const handleUnreadCountUpdate = useCallback((count: number) => {
    setTotalUnreadCount(count);
  }, []);

  // Handle typing updates - fix the callback signature
  const handleTypingUpdate = useCallback((updater: (prev: Record<string, { userId: string; conversationId: string; isTyping: boolean }>) => Record<string, { userId: string; conversationId: string; isTyping: boolean }>) => {
    setTypingUsers(updater);
  }, []);

  // Set up realtime subscriptions
  useChatRealtime(
    user?.id,
    currentCommunity?.id,
    currentConversation?.id || null,
    handleMessageReceived,
    handleUnreadCountUpdate,
    markMessagesAsRead
  );

  const { broadcastTyping } = useChatTyping(
    user?.id,
    currentCommunity?.id,
    handleTypingUpdate
  );

  // Initialize unread count on mount
  useEffect(() => {
    if (user?.id) {
      fetchUnreadCountOnly(user.id);
    }
  }, [user?.id]);

  const startConversation = async (counterpartId: string, userRole: string): Promise<Conversation> => {
    if (!user || !currentCommunity) {
      throw new Error('User or community not available');
    }

    console.log('[ChatContext] Starting conversation with counterpartId:', counterpartId, 'userRole:', userRole);
    
    // STEP 1: Immediately clear current state to prevent old messages from showing
    console.log('[ChatContext] Clearing current conversation state');
    setCurrentConversation(null);
    setLoadingMessages(true);
    
    // Set business context for this conversation - FIXED LOGIC
    if (userRole === CHAT_ROLE.BUSINESS_OWNER) {
      // If current user is business owner, use their own business ID
      if (userBusinessId) {
        setCurrentBusinessContext(userBusinessId);
      }
    } else {
      // If current user is community member, use the target business ID (counterpartId)
      setCurrentBusinessContext(counterpartId);
    }
    
    try {
      // First check if conversation already exists
      console.log('[ChatContext] Checking for existing conversation before creating new one');
      const existingConversation = await ConversationService.checkExistingConversation(
        counterpartId, 
        userRole, 
        user.id, 
        currentCommunity.id, 
        userBusinessId
      );
      
      if (existingConversation) {
        console.log('[ChatContext] Found existing conversation:', existingConversation.id);
        
        // STEP 2: Clear target conversation messages before loading new ones
        console.log('[ChatContext] Clearing messages for existing conversation:', existingConversation.id);
        setMessagesByConversation(prev => ({
          ...prev,
          [existingConversation.id]: [] // Clear target conversation messages
        }));
        
        await loadConversation(existingConversation.id);
        return existingConversation;
      }

      // No existing conversation found, create a new one
      console.log('[ChatContext] No existing conversation found, creating new one');
      
      if (!userBusinessId && userRole === CHAT_ROLE.BUSINESS_OWNER) {
        throw new Error('No business found for current user');
      }
      
      const conversationData = await ConversationService.createConversation(
        counterpartId,
        userRole,
        user.id,
        currentCommunity.id,
        userBusinessId!
      );
      
      console.log('[ChatContext] Successfully created new conversation:', conversationData.id);
      
      // STEP 2: Clear target conversation messages before loading
      console.log('[ChatContext] Clearing messages for new conversation:', conversationData.id);
      setMessagesByConversation(prev => ({
        ...prev,
        [conversationData.id]: [] // Clear target conversation messages
      }));
      
      await loadConversation(conversationData.id);
      
      return conversationData;
    } catch (error) {
      // Handle unique constraint violation gracefully
      if ((error as any)?.code === '23505') {
        console.log('[ChatContext] Unique constraint violation detected, fetching existing conversation');
        
        const existingConversation = await ConversationService.checkExistingConversation(
          counterpartId, 
          userRole, 
          user.id, 
          currentCommunity.id, 
          userBusinessId
        );
        if (existingConversation) {
          // Clear target conversation messages before loading
          setMessagesByConversation(prev => ({
            ...prev,
            [existingConversation.id]: []
          }));
          await loadConversation(existingConversation.id);
          return existingConversation;
        }
      }
      
      console.error('[ChatContext] Error starting conversation:', error);
      throw error;
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadConversation = async (conversationId: string): Promise<void> => {
    if (!user) return;

    console.log(`[ChatContext] Loading conversation with ID: ${conversationId}`);
    setLoadingMessages(true);
    
    try {
      const { conversation, messages } = await ConversationService.loadConversationData(
        conversationId,
        currentCommunity?.id || ''
      );
      
      console.log(`[ChatContext] Successfully fetched conversation data for ID: ${conversationId}`);
      console.log(`[ChatContext] Successfully fetched ${messages.length} messages for conversation ID: ${conversationId}`);
      
      const messagesToMark = messages
        .filter(msg => msg.receiver_id === user.id && !msg.read_at)
        .map(msg => msg.id);
      
      if (messagesToMark.length > 0) {
        console.log(`[ChatContext] Marking ${messagesToMark.length} messages as read`);
        await markMessagesAsRead(conversationId);
      }
      
      // STEP 3: Replace (not merge) messages for this specific conversation
      console.log(`[ChatContext] Replacing messages for conversation ${conversationId} with ${messages.length} new messages`);
      setMessagesByConversation(prev => ({
        ...prev,
        [conversationId]: messages // Replace, don't merge
      }));
      
      // STEP 4: Set current conversation only after messages are loaded
      setCurrentConversation(conversation);
      console.log(`[ChatContext] Conversation and messages state updated successfully`);
    } catch (error) {
      console.error('[ChatContext] Error loading conversation:', error);
      // Clear messages for this conversation on error
      setMessagesByConversation(prev => ({
        ...prev,
        [conversationId]: []
      }));
      throw error;
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadAllConversations = useCallback(async (): Promise<void> => {
    if (!user || !currentCommunity) {
      console.log('[ChatContext] Cannot load conversations: missing user or community');
      return;
    }
    
    if (loadingConversations) {
      console.log('[ChatContext] Already loading conversations, skip duplicate call');
      return;
    }
    
    if (isBusinessLoading) {
      console.log('[ChatContext] Business context is still loading, deferring conversation loading');
      return;
    }
    
    console.log('[ChatContext] Loading all conversations for user:', user.id, 'with userBusinessId:', userBusinessId);
    setLoadingConversations(true);
    try {
      let orCondition: string;
      
      if (userBusinessId) {
        orCondition = `member_id.eq.${user.id},business_id.eq.${userBusinessId}`;
        console.log('[ChatContext] Using OR condition with business ID:', orCondition);
      } else {
        orCondition = `member_id.eq.${user.id}`;
        console.log('[ChatContext] Using OR condition without business ID (user as member only):', orCondition);
      }
      
      const query = supabase
        .from('conversations')
        .select(`
          *,
          business:business_id (
            id,
            name,
            company_logo
          ),
          member:member_id (
            id,
            firstname,
            lastname,
            email,
            avatar_url
          )
        `)
        .eq('community_id', currentCommunity.id)
        .or(orCondition);
      
      const { data, error } = await query;
      
      if (error) {
        console.error('[ChatContext] Error in loadAllConversations:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        console.log('[ChatContext] No conversations found');
        setConversations([]);
        setTotalUnreadCount(0);
        return;
      }
      
      console.log('[ChatContext] Found', data.length, 'conversations');
      
      // Fetch usernames for all members in these conversations
      const memberIds = data.map(conv => conv.member_id).filter(Boolean);
      const { data: memberUsernames } = await supabase
        .from('community_members')
        .select('user_id, username')
        .in('user_id', memberIds)
        .eq('community_id', currentCommunity.id);
      
      // Create a map of user_id to username
      const usernameMap = new Map(
        memberUsernames?.map(member => [member.user_id, member.username]) || []
      );
      
      // Add usernames to member data
      data.forEach(conversation => {
        if (conversation.member && conversation.member_id) {
          (conversation.member as any).username = usernameMap.get(conversation.member_id);
        }
      });
      
      const conversationIds = data.map(conv => conv.id);
      
      const [lastMessagesResult, unreadCountResult, unreadMessagesResult] = await Promise.all([
        supabase
          .from('messages')
          .select('*')
          .in('conversation_id', conversationIds)
          .order('sent_at', { ascending: false }),
          
        supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('receiver_id', user.id)
          .is('read_at', null),
          
        supabase
          .from('messages')
          .select('conversation_id')
          .eq('receiver_id', user.id)
          .is('read_at', null)
          .in('conversation_id', conversationIds)
      ]);
      
      const lastMessagesByConversation: Record<string, Message> = {};
      if (!lastMessagesResult.error && lastMessagesResult.data) {
        lastMessagesResult.data.forEach(msg => {
          if (!lastMessagesByConversation[msg.conversation_id]) {
            lastMessagesByConversation[msg.conversation_id] = msg;
          }
        });
      } else if (lastMessagesResult.error) {
        console.error('[ChatContext] Error fetching last messages:', lastMessagesResult.error);
      }
      
      if (!unreadCountResult.error) {
        setTotalUnreadCount(unreadCountResult.count || 0);
      } else {
        console.error('[ChatContext] Error fetching total unread count:', unreadCountResult.error);
      }
      
      const unreadCountsByConversation: Record<string, number> = {};
      if (!unreadMessagesResult.error && unreadMessagesResult.data) {
        unreadMessagesResult.data.forEach(msg => {
          if (!unreadCountsByConversation[msg.conversation_id]) {
            unreadCountsByConversation[msg.conversation_id] = 0;
          }
          unreadCountsByConversation[msg.conversation_id]++;
        });
      } else if (unreadMessagesResult.error) {
        console.error('[ChatContext] Error fetching unread messages:', unreadMessagesResult.error);
      }
      
      const conversationsWithData = data.map(conversation => {
        return {
          ...conversation,
          lastMessage: lastMessagesByConversation[conversation.id] || null,
          unreadCount: unreadCountsByConversation[conversation.id] || 0
        } as Conversation;
      });
      
      // Sort conversations by most recent message
      const sortedConversations = conversationsWithData.sort((a, b) => {
        const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return bTime - aTime; // Most recent first
      });
      
      setConversations(sortedConversations);
      console.log(`[ChatContext] Updated conversations state with ${sortedConversations.length} conversations (sorted by most recent message)`);
    } catch (error) {
      console.error('[ChatContext] Error loading conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, [user?.id, currentCommunity?.id, userBusinessId, loadingConversations, isBusinessLoading]);

  const sendMessage = async (content: string, conversation?: Conversation): Promise<void> => {
    if (!user) {
      throw new Error('User not available');
    }
    
    // Use the passed conversation or the current one
    const targetConversation = conversation || currentConversation;
    
    if (!targetConversation) {
      throw new Error('No conversation available');
    }
    
    try {
      const userRole = targetConversation.member_id === user.id
        ? CHAT_ROLE.COMMUNITY_MEMBER
        : CHAT_ROLE.BUSINESS_OWNER;
      
      let receiverId: string;
      
      if (userRole === CHAT_ROLE.COMMUNITY_MEMBER) {
        const { data: businessOwner, error } = await supabase
          .from('businesses')
          .select('user_id')
          .eq('id', targetConversation.business_id)
          .single();
          
        if (error || !businessOwner?.user_id) {
          console.error('Error getting business owner:', error);
          throw new Error('Business owner not found');
        }
        
        receiverId = businessOwner.user_id;
      } else {
        receiverId = targetConversation.member_id;
      }
      
      console.log(`[ChatContext] Sending message to receiver: ${receiverId}`);
      
      const message = await ConversationService.sendMessage(
        targetConversation.id,
        user.id,
        receiverId,
        content
      );
      
      // Add message to the specific conversation
      setMessagesByConversation(prev => ({
        ...prev,
        [targetConversation.id]: [...(prev[targetConversation.id] || []), message]
      }));
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };

  const sendMessageWithImages = async (content: string, images: PendingImageUpload[], conversation?: Conversation): Promise<void> => {
    if (!user) {
      throw new Error('User not available');
    }
    
    // Use the passed conversation or the current one
    const targetConversation = conversation || currentConversation;
    
    if (!targetConversation) {
      throw new Error('No conversation available');
    }
    
    try {
      const userRole = targetConversation.member_id === user.id
        ? CHAT_ROLE.COMMUNITY_MEMBER
        : CHAT_ROLE.BUSINESS_OWNER;
      
      let receiverId: string;
      
      if (userRole === CHAT_ROLE.COMMUNITY_MEMBER) {
        const { data: businessOwner, error } = await supabase
          .from('businesses')
          .select('user_id')
          .eq('id', targetConversation.business_id)
          .single();
          
        if (error || !businessOwner?.user_id) {
          console.error('Error getting business owner:', error);
          throw new Error('Business owner not found');
        }
        
        receiverId = businessOwner.user_id;
      } else {
        receiverId = targetConversation.member_id;
      }
      
      console.log(`[ChatContext] Sending message with images to receiver: ${receiverId}`);
      
      // Helper function to convert File to base64
      const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = error => reject(error);
        });
      };
      
      // Prepare file data for deferred upload
      const filesData = await Promise.all(
        images.map(async (img, index) => ({
          data: await fileToBase64(img.file),
          originalFilename: img.file.name,
          fileSize: img.file.size,
          mimeType: img.file.type,
          displayOrder: index + 1
        }))
      );
      
      // Send message with files via the edge function (files will be uploaded on send)
      const result = await chatImageService.sendMessageWithImages({
        conversationId: targetConversation.id,
        content: content || '',
        receiverId,
        files: filesData
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send message with images');
      }
      
      // Add message to local state immediately with image data
      const messageWithImages = {
        ...result.message,
        images: result.message.images || [] // Use the images returned from the Edge function
      };
      
      setMessagesByConversation(prev => ({
        ...prev,
        [targetConversation.id]: [...(prev[targetConversation.id] || []), messageWithImages]
      }));
      
      console.log('[ChatContext] Message with images sent successfully and added to local state');
    } catch (error) {
      console.error('Error sending message with images:', error);
      throw error;
    }
  };

  const isUserTyping = (conversationId: string): boolean => {
    const typing = Object.values(typingUsers).some(
      status => status.conversationId === conversationId && status.isTyping
    );
    console.log('[ChatContext] Checking typing status:', {
      conversationId,
      typingUsers,
      isTyping: typing
    });
    return typing;
  };

  const setUserTyping = (conversationId: string, isTyping: boolean) => {
    broadcastTyping(conversationId, isTyping);
  };

  // Update checkExistingConversation to use correct business context - FIXED LOGIC
  const checkExistingConversation = async (counterpartId: string, userRole: string): Promise<Conversation | null> => {
    if (!user || !currentCommunity) return null;
    
    // Determine the correct business context based on user role
    const businessContextId = userRole === CHAT_ROLE.BUSINESS_OWNER ? userBusinessId : counterpartId;
    
    // Clear state for new business context without affecting current display
    if (businessContextId) {
      clearChatStateForNewBusiness(businessContextId);
    }
    
    return ConversationService.checkExistingConversation(
      counterpartId,
      userRole,
      user.id,
      currentCommunity.id,
      userBusinessId
    );
  };
  
  const fetchUnreadCountOnly = async (userId: string): Promise<number> => {
    if (fetchUnreadCountRef.current) {
      clearTimeout(fetchUnreadCountRef.current);
    }
    
    return new Promise((resolve) => {
      fetchUnreadCountRef.current = setTimeout(async () => {
        try {
          // Use role-based count: for the main header, we prioritize community member conversations
          // This way the header shows unread messages for conversations where the user is acting as a community member
          const count = await ConversationService.getUnreadCountForRole(userId, 'member');
          setTotalUnreadCount(count);
          resolve(count);
        } catch (error) {
          console.error('Error fetching unread count:', error);
          resolve(0);
        } finally {
          fetchUnreadCountRef.current = null;
        }
      }, 300);
    });
  };

  return (
    <ChatContext.Provider value={{
      conversations,
      loadingConversations,
      messagesByConversation,
      loadingMessages,
      currentConversation,
      startConversation,
      loadConversation,
      loadAllConversations,
      sendMessage,
      sendMessageWithImages,
      markMessagesAsRead,
      isUserTyping,
      setUserTyping,
      totalUnreadCount,
      checkExistingConversation,
      fetchUnreadCountOnly,
      getCurrentMessages
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export type { Message, Conversation };
